<template>
  <Dialog v-model:open="isOpen">
    <DialogScrollContent
      class="tw:max-w-3xl"
      is-static-backdrop
    >
      <DialogHeader>
        <DialogTitle>Send video reply to {{ videoUser.first_name }}</DialogTitle>
      </DialogHeader>
      <div class="tw:px-6">
        <div
          v-show="showRecorder"
        >
          <Tabs
            v-model="currentTab"
          >
            <TabsList
              v-show="isSupportVideoRecorder"
              class="tw:border-b-0 tw:mb-4"
            >
              <TabsTrigger
                :value="TAB_WEBCAM"
                variant="pill"
              >
                <i class="fal fa-camera tw:text-inherit" />
                <span>Webcam</span>
              </TabsTrigger>

              <TabsTrigger
                :value="TAB_UPLOAD"
                variant="pill"
              >
                <i class="fal fa-cloud-upload tw:text-inherit" />
                <span>Upload</span>
              </TabsTrigger>
            </TabsList>
            <TabsContent
              v-show="currentTab === TAB_WEBCAM"
              :value="TAB_WEBCAM"
              force-mount
            >
              <WebcamRecorder
                v-if="videoRecorderInitialized"
                ref="recorder"
                :hide-teleprompter="true"
                @record-finished="onRecordFinished"
                @unsupported="unsupportVideoRecorder"
              />
            </TabsContent>
            <TabsContent
              v-show="currentTab === TAB_UPLOAD"
              :value="TAB_UPLOAD"
              force-mount
            >
              <UploadVideoDropzone
                ref="uploadDropzone"
                @change-file="onChangeFile"
              />
            </TabsContent>
          </Tabs>
        </div>

        <template v-if="lastFile !== null && !uploading">
          <div class="form-group">
            <Input v-model="form.email" type="text" placeholder="Your Email (required)" :class="{ 'is-invalid': form.errors.has('email') }" />
            <div v-if="form.errors.has('email')" class="invalid-feedback">
              {{ form.errors.get('email') }}
            </div>
          </div>
          <div class="form-group">
            <Textarea
              v-model="form.note"
              rows="3"
              :placeholder="`Leave a note for ${videoUser.first_name} (optional)`"
            />
          </div>
          <button
            type="button"
            class="btn btn-primary btn-lg text-uppercase w-100"
            :disabled="uploading || form.busy"
            @click="send"
          >
            Send video reply
          </button>
        </template>

        <div v-if="uploading">
          <div class="progress mb-2">
            <div
              class="progress-bar progress-bar-success"
              role="progressbar"
              :aria-valuenow="uploadProgress"
              aria-valuemin="0"
              aria-valuemax="100"
              :style="{ width: `${uploadProgress}%` }"
              style="min-width: 2em;"
            >
              {{ Number(uploadProgress) }}%
            </div>
          </div>
          <p class="mb-0">
            Please do not close this page while video reply is uploading
          </p>
        </div>
      </div>
    </DialogScrollContent>
  </Dialog>
</template>

<script>
import { watchImmediate } from '@vueuse/core'
import { nextTick, ref, useTemplateRef } from 'vue'
import {
  Dialog,
  DialogHeader,
  DialogScrollContent,
  DialogTitle,
} from '@/shadcn/components/ui/dialog'
import { Input } from '@/shadcn/components/ui/input/index.js'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shadcn/components/ui/tabs/index.js'
import { Textarea } from '@/shadcn/components/ui/textarea/index.js'
import { is_null_or_empty } from '@/utils'
import UploadVideoDropzone from '../../common/upload-video-dropzone.vue'
import UploadVideoMixin from '../../common/upload-video-mixin'
import WebcamRecorder from '../create/webcam-recorder.vue'

export default {
  name: 'ReplyWithVideo',
  components: {
    Input,
    Textarea,
    TabsContent,
    TabsTrigger,
    TabsList,
    Tabs,
    DialogScrollContent,
    UploadVideoDropzone,
    WebcamRecorder,
    Dialog,
    DialogHeader,
    DialogTitle,
  },
  mixins: [UploadVideoMixin],
  props: {
    videoId: {
      required: true,
    },
    videoUser: {
      type: Object,
      required: true,
    },
    trackParams: {
      default: () => {
        return {}
      },
    },
  },
  setup() {
    const TAB_WEBCAM = 'tab-webcam'
    const TAB_UPLOAD = 'tab-upload'
    const currentTab = ref(TAB_WEBCAM)
    const isSupportVideoRecorder = ref(true)
    const videoRecorderInitialized = ref(false)
    const recorder = useTemplateRef('recorder')

    watchImmediate([currentTab, videoRecorderInitialized], ([newTab, initialized]) => {
      nextTick(() => {
        if (!initialized) {
          return
        }
        if (isSupportVideoRecorder.value) {
          if (newTab === TAB_WEBCAM) {
            recorder.value.capture()

            return
          }

          recorder.value.stopCapture()
        }

        if (newTab === TAB_WEBCAM && !isSupportVideoRecorder.value) {
          currentTab.value = TAB_UPLOAD
        }
      })
    })

    return {
      currentTab,
      isSupportVideoRecorder,
      videoRecorderInitialized,
      TAB_WEBCAM,
      TAB_UPLOAD,
    }
  },
  data() {
    return {
      showRecorder: true,
      uploading: false,
      lastFile: null,
      uploadPromise: null,
      isOpen: false,
      form: new SparkForm({
        disk: Spark.getDisk(),
        email: null,
        contact: null,
        campaign: null,
        video: '',
        preview: '',
        note: '',
      }),
    }
  },
  watch: {
    isOpen(newVal) {
      if (!newVal) {
        this.reset()
      }
    },
  },
  methods: {
    open() {
      this.showRecorder = true
      this.uploading = false
      this.form.loadData(this.trackParams)
      this.isOpen = true

      if (!this.videoRecorderInitialized) {
        this.videoRecorderInitialized = true
      }
      nextTick(() => {
        if (this.isSupportVideoRecorder) {
          this.currentTab = this.TAB_WEBCAM

          return
        }

        this.currentTab = this.TAB_UPLOAD
      })
    },
    unsupportVideoRecorder() {
      this.isSupportVideoRecorder = false
    },
    onChangeFile(file) {
      if (!this.uploading) {
        this.onRecordFinished(file)
      }
    },
    onRecordFinished(file) {
      this.lastFile = file
      this.showRecorder = false

      this.isOpen = true

      this.uploadPromise = this.uploadReplyVideo()
    },
    reset() {
      this.$refs.recorder.stopCapture()
      this.$refs.uploadDropzone.reset()
      this.videoRecorderInitialized = false
      this.showRecorder = true
      this.uploading = false
      this.uploadProgress = null
      this.lastFile = null
      this.form.reset()
    },

    async uploadReplyVideo() {
      try {
        this.form.resetStatus()

        this.form.video = await this.uploadVideo(this.lastFile, this.form.disk)
        this.form.preview = await this.videoUploader.uploadVideoReplyPreivew(this.videoId)
      }
      catch (_err) {
        await window.SwalTimer.fire({
          icon: 'error',
          title: 'Yikes! Something went wrong. Please try again.',
        })

        this.reset()
      }
    },

    async send() {
      if (is_null_or_empty(this.form.email)) {
        this.form.errors.set({ email: ['The email field is required.'] })
        return
      }

      try {
        this.uploading = true

        await this.uploadPromise
        await Spark.post(this.router('api.video.video_reply.store', { video: this.videoId }), this.form)

        this.reset()
        this.isOpen = false

        window.SwalTimer.fire({
          icon: 'success',
          title: 'Your video reply was sent',
        })
      }
      catch (_err) {
        this.uploading = false
      }
    },
  },
}
</script>
